import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, Box, CircularProgress, Button,
  Card, CardContent, Grid, TextField, Checkbox, FormControlLabel
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MetricLineChart from '../components/MetricLineChart';
import { useAuth } from '../contexts/AuthContext';
import { getMooringById, getMooringData } from '../services/mooringService';

// API kinds <-> UI labels
const VARS = [
  { key: 'cyanobacteria_index', label: 'Cyanobacteria Index' },
  { key: 'pc',                  label: 'Phycocyanin' },
  { key: 'chla',                label: 'Chlorophyll A' },
];

const MooringRollupPage = () => {
  const { mooringId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [mooring, setMooring] = useState(null);
  const [loadingMooring, setLoadingMooring] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [error, setError] = useState('');

  // variables selection (default to cyanobacteria_index checked)
  const [selectedVars, setSelectedVars] = useState({
    cyanobacteria_index: true,
    pc: false,
    chla: false,
  });

  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [chartData, setChartData] = useState([]); // wide rows

  const formatToLocalDateTimeString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${mi}`;
  };
  const convertToUTC = (localStr) => (localStr ? new Date(localStr).toISOString() : '');

  // pivot [{time, sensor_kind, sensor_value}] -> wide rows keyed by kind
  const pivotToWide = (rows) => {
    const byTime = new Map();
    for (const r of rows || []) {
      if (!r || !r.time) continue;
      const t = r.time;
      if (!byTime.has(t)) byTime.set(t, { time: t });
      const key = (r.sensor_kind || '').toLowerCase();
      byTime.get(t)[key] = r.sensor_value;
    }
    return Array.from(byTime.values()).sort((a, b) => new Date(a.time) - new Date(b.time));
  };

  // init 30‑min window
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setMinutes(start.getMinutes() - 30);
    setEndDateTime(formatToLocalDateTimeString(end));
    setStartDateTime(formatToLocalDateTimeString(start));
  }, []);

  // fetch mooring (expects mooring.status.* if your serializer includes it)
  useEffect(() => {
    const run = async () => {
      try {
        const data = await getMooringById(mooringId, token);
        setMooring(data);
      } catch (e) {
        console.error(e);
        setError('Failed to fetch mooring details');
      } finally {
        setLoadingMooring(false);
      }
    };
    if (token) run();
  }, [mooringId, token]);

  const handleVarChange = (e) => {
    const { name, checked } = e.target;
    setSelectedVars((prev) => ({ ...prev, [name]: checked }));
  };

  const handleUpdateCharts = async () => {
    try {
      setLoadingMetrics(true);
      setError('');

      const kinds = VARS.filter(v => selectedVars[v.key]).map(v => v.key);
      if (!kinds.length) {
        setError('Please select at least one variable');
        setLoadingMetrics(false);
        return;
      }

      const rows = await getMooringData(
        mooringId,
        {
          kinds,
          startDate: convertToUTC(startDateTime),
          endDate: convertToUTC(endDateTime),
        },
        token
      );

      setChartData(pivotToWide(rows));
    } catch (e) {
      console.error('Error fetching rollup data:', e);
      setError('Failed to fetch rollup data');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleBack = () => navigate('/dashboard');

  const labelFor = (key) => (VARS.find(v => v.key === key)?.label || key);

  const fmtTS = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

  if (loadingMooring) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button startIcon={<ArrowBackIcon />} variant="contained" sx={{ mt: 2 }} onClick={handleBack}>
          Back to Dashboard
        </Button>
      </Paper>
    );
  }

  return (
    <>
      {/* Header + Mooring Info */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" sx={{ mb: 2 }} onClick={handleBack}>
          Back
        </Button>

        {mooring && (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {mooring.name || `Mooring ${mooringId}`}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Mooring Details</Typography>
                    <Typography variant="body1"><strong>ID:</strong> {mooring.id}</Typography>
                    {mooring.grid && (
                      <Typography variant="body1"><strong>Grid:</strong> {mooring.grid.name || mooring.grid.id}</Typography>
                    )}
                    {mooring.status && (
                      <>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          <strong>Latitude:</strong> {mooring.status.latitude ?? '—'}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Longitude:</strong> {mooring.status.longitude ?? '—'}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Positioned At:</strong> {fmtTS(mooring.status.positioned_at)}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Latest Sync:</strong> {fmtTS(mooring.status.latest_sync)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Variables + Time Range + Charts */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>Variables</Typography>

        {/* Variables selection */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {VARS.map(v => (
              <FormControlLabel
                key={v.key}
                control={
                  <Checkbox
                    name={v.key}
                    checked={!!selectedVars[v.key]}
                    onChange={handleVarChange}
                  />
                }
                label={v.label}
              />
            ))}
          </Box>
        </Paper>

        {/* Time Range */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={3}>
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                fullWidth
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { max: new Date().toISOString().slice(0, 16) } }}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              <TextField
                label="End Date & Time"
                type="datetime-local"
                fullWidth
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { max: new Date().toISOString().slice(0, 16) } }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateCharts}
                  sx={{ whiteSpace: 'nowrap', minWidth: '150px' }}
                >
                  Update Charts
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
          {chartData.length
            ? `Showing data from ${new Date(startDateTime).toLocaleString()} to ${new Date(endDateTime).toLocaleString()}`
            : 'Pick variables and a time range, then click "Update Charts".'}
        </Typography>

        {loadingMetrics ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : !chartData.length ? (
          <Typography variant="body1" color="textSecondary" align="center">
            No data available for the selected variables/time window.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {VARS.filter(v => selectedVars[v.key]).map(v => (
              <Grid item xs={12} key={v.key}>
                <MetricLineChart
                  title={v.label}
                  data={chartData}
                  metricKey={v.key}  // wide rows use lowercased keys
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </>
  );
};

export default MooringRollupPage;
